import * as yup from 'yup';
import _isNil from 'lodash/isNil';
import _isEmpty from 'lodash/isEmpty';
import { Row, RenderConfig, SelectInput, TextInput, NumberInput, ASSET_TYPE, AssetUploadInput } from 'commons-ui';
import { GAME_OPTIONS } from 'pages/bulkActions/bulkRefund/utils/constants';
import { CONFISCATION_AND_REFUND_REQUESTS_OPTIONS, CONFISCATION_TYPE_OPTIONS } from '../types';
import { SAMPLE_COLUMNS_NAME_CONFISCATION_DETAILS, SAMPLE_COLUMNS_VALUES } from '../constants';

export const FORM_FIELD_KEYS = {
	ZUPEE_USER_ID: 'zupeeUserId',
	TRANSACTION_ID: 'transactionId',
	REFUND_TYPE: 'refundType',
	CONFISCATION_TYPE: 'confiscationType',
	AMOUNT: 'amount',
	FRESHDESK_TICKET_ID: 'freshdeskTicketId',
	REPORTED_ON: 'reportedOn'
};

export const validationSchema = yup.object().shape(
	{
		uid: yup.number().required('Zupee User Id is a required field'),
		transactionId: yup.number().required('Transaction Id is a required field'),
		refundType: yup.string().required('Refund Type is a required field'),
		confiscationType: yup.string().required('Confiscation Type is a required field'),
		amount: yup
			.string()
			.nullable()
			.notRequired()
			.when('amount', {
				is: (value: string) => !_isNil(value) && !_isEmpty(value),
				then: yup
					.string()
					.test('maxDigitsAfterDecimal', 'number field must have 2 digits after decimal or less', (amountVal: any) =>
						/^\d+(\.\d{1,2})?$/.test(amountVal)
					)
			}),
		freshdeskTicketId: yup.string().required('Freshdesk TicketId is a required field'),
		reportedOn: yup.number().required('Reported On is a required field')
	},
	[['amount', 'amount']]
);

export const FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS.ZUPEE_USER_ID]: new RenderConfig()
		.setName('uid')
		.setLabel('Zupee User Id')
		.setRenderer(NumberInput)
		.setRenderProps({ isEditable: { create: true, edit: false }, required: true }),
	[FORM_FIELD_KEYS.TRANSACTION_ID]: new RenderConfig()
		.setName('transactionId')
		.setLabel('Transaction Id')
		.setRenderer(NumberInput)
		.setRenderProps({ isEditable: { create: true, edit: false }, required: true }),
	[FORM_FIELD_KEYS.REFUND_TYPE]: new RenderConfig()
		.setName('refundType')
		.setLabel('Refund Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true,
			options: CONFISCATION_AND_REFUND_REQUESTS_OPTIONS
		}),
	[FORM_FIELD_KEYS.CONFISCATION_TYPE]: new RenderConfig()
		.setName('confiscationType')
		.setLabel('Confiscation Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true,
			options: CONFISCATION_TYPE_OPTIONS
		}),
	[FORM_FIELD_KEYS.AMOUNT]: new RenderConfig()
		.setName('amount')
		.setLabel('Amount (₹)')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: false }, required: true }),
	[FORM_FIELD_KEYS.FRESHDESK_TICKET_ID]: new RenderConfig()
		.setName('freshdeskTicketId')
		.setLabel('Freshdesk Ticket Id')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: false }, required: true }),
	[FORM_FIELD_KEYS.REPORTED_ON]: new RenderConfig()
		.setName('reportedOn')
		.setLabel('Reported On')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true,
			inputType: 'date'
		})
};

export const FORM_LAYOUT_CONFIG = [
	new Row(FORM_FIELD_KEYS.ZUPEE_USER_ID),
	new Row(FORM_FIELD_KEYS.TRANSACTION_ID),
	new Row(FORM_FIELD_KEYS.REFUND_TYPE),
	new Row(FORM_FIELD_KEYS.CONFISCATION_TYPE),
	new Row(FORM_FIELD_KEYS.FRESHDESK_TICKET_ID),
	new Row(FORM_FIELD_KEYS.AMOUNT),
	new Row(FORM_FIELD_KEYS.REPORTED_ON)
];

export const FORM_FIELD_KEYS_REJECT_REQUEST = {
	REJECTION_REASON: 'rejectionReason'
};

export const validationSchemaRejectRequest = yup.object().shape({
	rejectionReason: yup.string().required('Rejection reason is a required field')
});

export const FORM_FIELD_CONFIG_REJECT_REQUEST = {
	[FORM_FIELD_KEYS_REJECT_REQUEST.REJECTION_REASON]: new RenderConfig()
		.setName('rejectionReason')
		.setLabel('Rejection reason')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: false }, required: true })
};

export const validationSchemaForBulkConfiscation = yup.object().shape({
	gameName: yup.string().required('Game name is a required field'),
	csvName: yup.string().required('CSV Name is a required field'),
	description: yup.string().required('Description is a required field'),
	csv: yup.mixed().test('file', 'Csv file is required', (file) => {
		if (file && file instanceof File && file.name.endsWith('.xlsx')) {
			return true;
		}
		return false;
	})
});

export const BULK_FORM_FIELD_KEYS = {
	CSV_NAME: 'csvName',
	DESCRIPTION: 'description',
	CSV_URL: ASSET_TYPE.CSV,
	GAME_NAME: 'gameName'
};

const downloadSampleCsvFile = () => {
	const csvString = [SAMPLE_COLUMNS_NAME_CONFISCATION_DETAILS, SAMPLE_COLUMNS_VALUES].map((e) => e.join(',')).join('\n');
	const csvContent = `data:text/csv;charset=utf-8, ${csvString}`;
	const encodedUri = encodeURI(csvContent);

	const link = document.createElement('a');
	link.setAttribute('href', encodedUri);
	document.body.appendChild(link);

	return link;
};

export const BULK_FORM_FIELD_CONFIG = {
	[BULK_FORM_FIELD_KEYS.GAME_NAME]: new RenderConfig()
		.setName('gameName')
		.setLabel('Game Name')
		.setRenderer(SelectInput)
		.setRenderProps({ isEditable: { create: true, edit: false }, options: GAME_OPTIONS, required: true }),

	[BULK_FORM_FIELD_KEYS.CSV_NAME]: new RenderConfig()
		.setName('csvName')
		.setLabel('CSV Name')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: false }, required: true }),

	[BULK_FORM_FIELD_KEYS.DESCRIPTION]: new RenderConfig()
		.setName('description')
		.setLabel(`Description (Special characters are not allowed eg: / , * , $, _ , - , etc )`)
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: false },
			required: true
		}),
	[BULK_FORM_FIELD_KEYS.CSV_URL]: new RenderConfig()
		.setName(ASSET_TYPE.CSV)
		.setLabel('Upload CSV File')
		.setRenderer(AssetUploadInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			assetType: ASSET_TYPE.EXCEL,
			downloadFile: true,
			displayText: 'Sample Excel File',
			link: downloadSampleCsvFile()
		})
};

export const BULK_FORM_LAYOUT_CONFIG = [
	new Row(BULK_FORM_FIELD_KEYS.GAME_NAME),
	new Row(BULK_FORM_FIELD_KEYS.CSV_NAME),
	new Row(BULK_FORM_FIELD_KEYS.DESCRIPTION),
	new Row(BULK_FORM_FIELD_KEYS.CSV_URL)
];
export const FORM_LAYOUT_CONFIG_REJECT_REQUEST = [new Row(FORM_FIELD_KEYS_REJECT_REQUEST.REJECTION_REASON)];
